"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useStytch } from "@stytch/nextjs";
import { useConvexAuth } from "convex/react";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DrawerFooter,
} from "@/components/ui/drawer";
import { Separator } from "@/components/ui/separator";
import { Settings, CreditCard, LogOut, ExternalLink, History } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAction, useQuery } from "convex/react";
import { api } from "@repo/backend/convex/_generated/api";

export interface MobileUserMenuProps {}

export function MobileUserMenu(_props: MobileUserMenuProps) {
  const { isAuthenticated } = useConvexAuth();
  const stytch = useStytch();
  const signOut = async () => {
    await stytch.session.revoke();
    if (typeof window !== "undefined") window.location.href = "/";
  };
  const openBillingPortal = useAction(api.autumn.openBillingPortal);
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const userInfo = useQuery(api.users.getMyInfo);

  async function handleOpenBillingPortal() {
    try {
      const ret = typeof window !== "undefined" ? window.location.href : undefined;
      const { url } = await openBillingPortal({ returnUrl: ret });
      if (!url) throw new Error("No billing portal URL");
      window.open(url, "_blank", "noopener,noreferrer");
      setOpen(false);
    } catch (e) {
      console.error("Failed to open billing portal", e);
      router.push("/dashboard/billing");
      setOpen(false);
    }
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Avatar className="size-8 cursor-pointer rounded-lg">
          <AvatarImage src={userInfo?.avatar ?? undefined} alt={userInfo?.name ?? "User"} />
          <AvatarFallback className="rounded-lg">
            {(userInfo?.name?.[0] ?? "U").toUpperCase()}
          </AvatarFallback>
        </Avatar>
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader className="pb-2">
          <DrawerTitle>Account</DrawerTitle>
        </DrawerHeader>
        <div className="px-4 pb-3 space-y-1">
          {isAuthenticated ? (
            <>
              <Link
                href="/dashboard/history"
                className="flex items-center gap-3 rounded-lg px-3 py-3 hover:bg-muted"
                onClick={() => setOpen(false)}
              >
                <History className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm">Session history</span>
              </Link>

              <Link
                href="/dashboard/settings"
                className="flex items-center gap-3 rounded-lg px-3 py-3 hover:bg-muted"
                onClick={() => setOpen(false)}
              >
                <Settings className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm">Settings</span>
              </Link>

              <Link
                href="/dashboard/billing"
                className="flex items-center gap-3 rounded-lg px-3 py-3 hover:bg-muted"
                onClick={() => setOpen(false)}
              >
                <CreditCard className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm">Billing</span>
              </Link>

              <button
                type="button"
                className="w-full text-left flex items-center gap-3 rounded-lg px-3 py-3 hover:bg-muted"
                onClick={handleOpenBillingPortal}
              >
                <ExternalLink className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm">Manage Subscription</span>
              </button>

              <Separator className="my-1" />

              <button
                type="button"
                className="w-full text-left flex items-center gap-3 rounded-lg px-3 py-3 hover:bg-muted"
                onClick={() => {
                  void signOut().catch(console.error);
                  setOpen(false);
                }}
              >
                <LogOut className="h-5 w-5" />
                <span className="text-sm">Sign out</span>
              </button>
            </>
          ) : (
            <div className="flex items-center justify-between gap-2 px-1">
              <Link href="/textbook" onClick={() => setOpen(false)} className="flex-1">
                <Button variant="ghost" className="w-full">Sign In</Button>
              </Link>
              <Link href="/textbook" onClick={() => setOpen(false)} className="flex-1">
                <Button className="w-full">Sign Up</Button>
              </Link>
            </div>
          )}
        </div>
        <DrawerFooter className="pt-0">
          <DrawerClose asChild>
            <Button variant="secondary">Close</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
